import React from 'react';
import HowItWorksPage from '../components/HowItWorksPage';
import { StatusCards, LifecycleStepper, InfoCallout } from '../components/HelpArticleLayout';

// Briefs · How it all works — the staff-side explainer for the brief lifecycle.
// Written from the code: a brief has three statuses (draft / sent / closed) and
// everything else people call a "status" is a derived view of those plus the
// recipients' own states.

const ICON = {
  map:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>,
  steps:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  users:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></svg>,
  chat:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>,
  edit:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  award:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6" /><path d="M15.5 12.5 17 22l-5-3-5 3 1.5-9.5" /></svg>,
  box:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>,
};

const sections = [
  {
    id: 'picture', title: 'What a brief is', icon: ICON.map,
    body: (
      <>
        <p>A brief is one piece of work put out to a set of suppliers, their answers gathered in one place, and a record of who won and what happened.</p>
        <p>It has only <strong className="text-ink">three</strong> real statuses. Everything else the app shows you — at risk, overdue, awaiting responses, awarded — is a <em>view</em> of those three plus what the recipients have done.</p>
      </>
    ),
  },
  {
    id: 'lifecycle', title: 'The three statuses', icon: ICON.steps,
    body: (
      <>
        <LifecycleStepper
          steps={[
            { color: '#51534A', label: 'Draft', description: 'Being written. No supplier can see it. Edit freely.' },
            { color: '#2E8FA1', label: 'Sent', description: 'Live with its recipients. They can respond until the deadline.' },
            { color: '#009159', label: 'Closed', description: 'Finished — either awarded to a supplier, or closed with no winner.' },
          ]}
        />
        <p>&ldquo;Awarded&rdquo; is not a fourth status. It is a <strong className="text-ink">closed</strong> brief that also has an awarded supplier on it, which is why the Briefs list offers Awarded and Closed-without-award as a sub-filter rather than as top-level states.</p>
      </>
    ),
  },
  {
    id: 'recipients', title: 'Recipients, and what each one is doing', icon: ICON.users,
    body: (
      <>
        <p>Sending a brief creates a recipient row per supplier. Their state is theirs, not the brief&rsquo;s:</p>
        <StatusCards items={[
          { color: '#E89A2E', textColor: '#8A5A12', label: 'Awaiting', description: 'Sent, not opened or not started. The action is with them.' },
          { color: '#2E8FA1', label: 'In progress', description: 'They have started a response but not submitted it.' },
          { color: '#2E8FA1', label: 'Submitted', description: 'Their quote is in and can be compared.' },
          { color: '#C6503A', label: 'Declined', description: 'They have said no. They stop being chased.' },
        ]} />
        <InfoCallout tone="warn">A supplier who is <strong>red on compliance cannot be awarded</strong>. They can still be sent a brief and can still respond — the block is on awarding, not on asking.</InfoCallout>
      </>
    ),
  },
  {
    id: 'chasing', title: 'Deadlines, chasing and questions', icon: ICON.chat,
    body: (
      <>
        <p>Every sent brief carries a response deadline. Two derived states come off it, and both appear in the page banner and the daily round-up:</p>
        <ul className="list-disc pl-5 space-y-1.5 my-1">
          <li><strong className="text-ink">At risk</strong> — live, deadline close, and people still haven&rsquo;t answered.</li>
          <li><strong className="text-ink">Overdue</strong> — the deadline passed with no decision recorded. Nobody is waiting on you for a reply; it just needs closing off.</li>
        </ul>
        <p>Suppliers can ask questions against a brief without breaking cover — a question and its answer can be shared with everyone on the brief, so one supplier&rsquo;s clarification doesn&rsquo;t become an advantage.</p>
      </>
    ),
  },
  {
    id: 'amending', title: 'Amending a brief that has gone out', icon: ICON.edit,
    body: (
      <>
        <p>A sent brief can still be changed, and the system treats that seriously because someone may already have priced the old version.</p>
        <ul className="list-disc pl-5 space-y-1.5 my-1">
          <li>Every amendment is a <strong className="text-ink">version</strong>, and the changes between versions are viewable.</li>
          <li>Recipients get one banner covering everything that changed, not one per edit.</li>
          <li>A quote submitted against an earlier version is <strong className="text-ink">flagged as stale</strong> on the comparison screen, so you never compare a price for one scope against a price for another.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'award', title: 'Awarding, and the close-down that follows', icon: ICON.award,
    body: (
      <>
        <p>Awarding closes the brief and names a supplier. It is not the end of the record. Four things are then tracked before the brief can be put away:</p>
        <ul className="list-disc pl-5 space-y-1.5 my-1">
          <li><strong className="text-ink">Final supplier quote on file</strong> — the one they actually invoiced against.</li>
          <li><strong className="text-ink">Final cost checked</strong> — somebody confirming the figure, which is a separate act from typing it.</li>
          <li><strong className="text-ink">Post-event feedback recorded</strong> — four questions about how they did.</li>
          <li><strong className="text-ink">Full debrief required?</strong> — a yes/no judgement, because the absence of a debrief means &ldquo;not written&rdquo;, never &ldquo;not needed&rdquo;.</li>
        </ul>
        <InfoCallout>Each step is derived from a real record, so nothing can claim to be done when the underlying thing is missing. The two money items warn you on archive; feedback and the debrief do not block, because both depend on people who may be on site.</InfoCallout>
      </>
    ),
  },
  {
    id: 'archiving', title: 'Archiving', icon: ICON.box,
    body: (
      <>
        <p>Archiving hides a brief from everyone&rsquo;s working view and from every supplier&rsquo;s portal. It is reversible, but it is the point of no easy return — which is why the close-down checklist sits <em>before</em> it and names anything still outstanding when you archive anyway.</p>
        <p>A debrief belongs to a brief, one each, and is raised from the brief itself rather than from a menu item. It is internal and never visible to a supplier.</p>
      </>
    ),
  },
];

export default function BriefsInfo() {
  return (
    <HowItWorksPage
      sections={sections}
      intro="The brief lifecycle end to end — the three statuses everything else is derived from, what each recipient's state means, how amendments protect a quote, and the close-down that has to happen before a brief is put away."
      backTo="/briefs"
      backLabel="Briefs"
    />
  );
}
